import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ViolationManagementInterface = ({ violations, loading, onStartRemediation, onViewTimeline, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('active');
  const [expandedViolation, setExpandedViolation] = useState(null);

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-error',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-success'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  const getSeverityBg = (severity) => {
    const backgrounds = {
      critical: 'bg-error/10 border-error/30',
      high: 'bg-warning/10 border-warning/30',
      medium: 'bg-primary/10 border-primary/30',
      low: 'bg-success/10 border-success/30'
    };
    return backgrounds?.[severity] || 'bg-muted border-border';
  };

  const getViolationExplanation = (violationType) => {
    const explanations = {
      minimum_wage_violation: 'Your current hourly rate is below the PNG minimum wage requirement. This must be corrected immediately to comply with the Employment Act.',
      overtime_rate_violation: 'Overtime hours are not being compensated at the required 1.5x multiplier as mandated by PNG Employment Act.',
      leave_policy_violation: 'Leave entitlements do not meet the minimum requirements specified in the Employment Act.',
      nasfund_contribution_violation: 'NASFUND contributions are not being calculated correctly according to current rates.'
    };
    return explanations?.[violationType] || 'This violation requires immediate attention to maintain compliance.';
  };

  const getRemediationSteps = (violationType) => {
    const steps = {
      minimum_wage_violation: [
        'Review current hourly rates for affected employees',
        'Calculate backpay owed from violation date to present',
        'Update payroll system with correct minimum wage rates',
        'Process backpay in next payroll cycle',
        'Verify compliance with updated rates'
      ],
      overtime_rate_violation: [
        'Identify all overtime hours worked during violation period',
        'Recalculate overtime pay at correct 1.5x multiplier',
        'Calculate total backpay amount owed',
        'Update overtime calculation rules in payroll system',
        'Process backpay and verify corrections'
      ],
      leave_policy_violation: [
        'Review current leave balances for affected employees',
        'Calculate correct leave entitlements per Employment Act',
        'Update leave balances in HR system',
        'Notify employees of corrected balances',
        'Implement automated leave accrual tracking'
      ]
    };
    return steps?.[violationType] || ['Contact HR support for remediation guidance'];
  };

  const filteredViolations = violations?.filter(v => {
    if (activeTab === 'active') {
      return v?.violationStatus === 'identified' || v?.violationStatus === 'in_progress';
    } else if (activeTab === 'resolved') {
      return v?.violationStatus === 'completed';
    }
    return true;
  });

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mb-4" />
          <p className="text-sm text-muted-foreground">Loading your violations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex items-center gap-1 p-2">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'active' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name="AlertCircle" size={16} className="inline mr-2" />
            Active ({violations?.filter(v => v?.violationStatus !== 'completed')?.length})
          </button>
          <button
            onClick={() => setActiveTab('resolved')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'resolved' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name="CheckCircle2" size={16} className="inline mr-2" />
            Resolved ({violations?.filter(v => v?.violationStatus === 'completed')?.length})
          </button>
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        {filteredViolations?.length === 0 ? (
          <div className="text-center py-12">
            <Icon 
              name={activeTab === 'resolved' ? 'CheckCircle2' : 'Shield'} 
              size={64} 
              color={activeTab === 'resolved' ? 'var(--color-success)' : 'var(--color-muted-foreground)'} 
              className="mx-auto mb-4 opacity-50" 
            />
            <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
              {activeTab === 'resolved' ? 'No Resolved Violations' : 'No Active Violations'}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {activeTab === 'resolved' ?'Resolved violations will appear here' :'You are currently compliant with PNG Employment Act'
              }
            </p>
            <Button variant="outline" size="sm" onClick={onRefresh} iconName="RefreshCw">
              Refresh
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {filteredViolations?.map((violation, index) => (
                <motion.div
                  key={violation?.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`border rounded-lg overflow-hidden ${getSeverityBg(violation?.severity)}`}
                >
                  {/* Violation Header */}
                  <div className="p-4 bg-card/50">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon 
                            name="AlertTriangle" 
                            size={20} 
                            color={`var(--color-${violation?.severity === 'critical' ? 'error' : violation?.severity === 'high' ? 'warning' : 'primary'})`}
                          />
                          <h4 className="font-heading font-semibold text-base text-foreground">
                            {violation?.violationType?.replace(/_/g, ' ')?.toUpperCase()}
                          </h4>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          {getViolationExplanation(violation?.violationType)}
                        </p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-bold rounded-full ${getSeverityColor(violation?.severity)} bg-card border border-current`}>
                        {violation?.severity?.toUpperCase()}
                      </span>
                    </div>

                    {/* Violation Details */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="p-3 bg-card rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Affected Employees</p>
                        <p className="text-lg font-bold text-foreground">{violation?.affectedCount}</p>
                      </div>
                      <div className="p-3 bg-card rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Financial Impact</p>
                        <p className="text-lg font-bold text-warning">K{violation?.estimatedCost?.toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Regulation Reference */}
                    {violation?.regulationReference && (
                      <div className="p-3 bg-card rounded-lg mb-4">
                        <p className="text-xs text-muted-foreground mb-1">PNG Employment Act Reference</p>
                        <p className="text-sm font-medium text-foreground">{violation?.regulationReference}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                      {violation?.violationStatus !== 'completed' && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => onStartRemediation(violation)}
                          iconName="Zap"
                        >
                          Start AI Remediation
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewTimeline?.(violation)}
                        iconName="GitBranch"
                      >
                        View Timeline
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExpandedViolation(expandedViolation === violation?.id ? null : violation?.id)}
                        iconName={expandedViolation === violation?.id ? 'ChevronUp' : 'ChevronDown'}
                      >
                        {expandedViolation === violation?.id ? 'Hide' : 'View'} Steps
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Remediation Steps */}
                  {expandedViolation === violation?.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-border/50 p-4 bg-card"
                    >
                      <h5 className="font-heading font-semibold text-sm text-foreground mb-3">
                        Step-by-Step Remediation Guide
                      </h5>
                      <ol className="space-y-2">
                        {getRemediationSteps(violation?.violationType)?.map((step, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <p className="text-sm text-muted-foreground flex-1">{step}</p>
                          </li>
                        ))}
                      </ol>
                    </motion.div>
                  )}

                  {/* Timestamp */}
                  <div className="px-4 py-2 bg-muted/30 border-t border-border/50">
                    <div className="flex items-center gap-2">
                      <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                      <p className="text-xs text-muted-foreground">
                        Detected {new Date(violation?.identifiedAt)?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViolationManagementInterface;